/* eslint-disable import/no-cycle */
import {observer} from 'mobx-react-lite';
import {useState} from 'react';
import {faInfo} from '@fortawesome/pro-solid-svg-icons';
import {WidgetLayout} from '../../models/widgetLayout';
import {WidgetLayouts} from '../shared/widgets/WidgetLayouts';
import ModelProperties from '../shared/descriptions/ModelProperties';
import OperatorWidget from '../operator/shared/OperatorWidget';
import ActiveOperationDetailModal from './ActiveOperationDetailModal';
import {ActiveOperationWidgetConfig} from './activeOperationWidgetConfig';
import {useMount} from '../../hooks/useMount';
import ActiveOperationDetailsPhaseProgress from './ActiveOperationDetailsPhaseProgress';
import {Barcode} from '../shared/Barcode';
import AbortOperationButton from './AbortOperationButton';
import {useStore} from '../../hooks/useStore';
import {ScopeContext} from '../../policies/scopeContext';
import {operationAbort, orderManagement, workflow} from '../../models/scope';
import {useModulePolicy} from '../../hooks/useModulePolicy';
import {EnDash} from '../shared/unicodeWrapper/EnDash';
import './ActiveOperationDetailsWidget.scss';

const ActiveOperationDetailsWidget = ({disabled, minimized = false, identifier}) => {
  const store = useStore();
  const [modalVisible, setModalVisible] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const widgetConfig = new ActiveOperationWidgetConfig(store, ActiveOperationDetailsWidget.identifier);
  const policy = useModulePolicy(store, [operationAbort]);

  // Sometimes, custom properties are loaded too late. This updates the component once, they are available.
  useMount(() => {
    setRefresh(!refresh);
  }, [store.propertyStore.properties.length]);

  const barcodeProperties = widgetConfig.getBarcodePropertyKeys();
  let properties = widgetConfig.getWidgetProperties();

  // add barcode renderer
  properties = properties.map((property) => {
    const newProperty = {...property};
    if (barcodeProperties.includes(property.key)) {
      newProperty.render = (text, model) => {
        const value = property.render(text, model);
        if (value.children === EnDash() || value.children === '') {
          return EnDash();
        }
        return {
          children: <Barcode value={value.children}/>,
        };
      };
    }
    return newProperty;
  });

  return (
    <ScopeContext.Provider value={[workflow, orderManagement]}>
      <OperatorWidget
        onHeadClick={store.operationStore.active ? () => setModalVisible(true) : undefined}
        title={widgetConfig.getWidgetSetting('title')}
        disabled={disabled}
        minimized={minimized}
        identifier={identifier}
        isPinable
        widgetConfig={widgetConfig}
      >
        <ModelProperties
          loading={store.operationStore.isLoadingCollection}
          properties={properties}
          model={store.operationStore.active}
          className={'active-operation-properties'}
        />
        <ActiveOperationDetailsPhaseProgress widgetConfig={widgetConfig}/>
        {policy.canExecute() && store.operationStore.active
        && <AbortOperationButton operation={store.operationStore.active}/>}
      </OperatorWidget>
      {modalVisible && <ActiveOperationDetailModal onCancel={() => setModalVisible(false)}/>}
    </ScopeContext.Provider>
  );
};

ActiveOperationDetailsWidget.icon = faInfo;
ActiveOperationDetailsWidget.isPinable = true;

ActiveOperationDetailsWidget.identifier = 'ActiveOperationDetailsWidget';
ActiveOperationDetailsWidget.defaultLayout = new WidgetLayout(
  {
    identifier: ActiveOperationDetailsWidget.identifier,
    x: 0,
    y: 0,
    height: 10,
    minHeight: 1,
    width: WidgetLayouts.thirdWidth.w,
    minWidth: WidgetLayouts.thirdWidth.minW,
  }
);

export default observer(ActiveOperationDetailsWidget);
